import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, BadgePercent } from "lucide-react";
import {
  getBusinessDetails,
  updateBusinessDiscount,
  resetUpdateStatus,
} from "../../store/slices/businessOwnerSlice";
import { BusinessOwnerDiscountEditForm } from "../../components/BusinessOwnerEditForms";
import ReusableModal, { MODAL_TYPES } from "../../components/ReusableModal";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";
import usePremiumStatus from "../../hooks/usePremiumStatus";

export default function BusinessDiscount() {
  const dispatch = useDispatch();
  const { isPremium } = usePremiumStatus();
  const { business, loading, updateStatus, updateError } = useSelector(
    (state) => state.businessOwner
  );

  const [modalState, setModalState] = useState({
    open: false,
    type: null,
    loading: false,
  });

  useEffect(() => {
    dispatch(getBusinessDetails());
  }, [dispatch]);

  useEffect(() => {
    if (updateStatus === "success") {
      showApiSuccess("Discount updated successfully");
      setModalState({ open: false, type: null, loading: false });
      dispatch(resetUpdateStatus());
      dispatch(getBusinessDetails());
    } else if (updateStatus === "failed") {
      showApiError(null, updateError);
      setModalState((prev) => ({ ...prev, loading: false }));
      dispatch(resetUpdateStatus());
    }
  }, [updateStatus, updateError, dispatch]);

  const openModal = () => {
    setModalState({
      open: true,
      type: MODAL_TYPES.CUSTOM,
      loading: false,
    });
  };

  const closeModal = () => {
    setModalState({
      open: false,
      type: null,
      loading: false,
    });
  };

  const handleSaveDiscount = async (data) => {
    setModalState((prev) => ({ ...prev, loading: true }));
    await dispatch(updateBusinessDiscount(data));
  };

  if (loading && !business) {
    return <Loader />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Discount Information
            </h2>
            <p className="text-gray-600 mt-1">
              Manage your business discount settings and offers
            </p>
          </div>
        </div>

        {business ? (
          <div className="space-y-6">
            {/* Discount Information Card */}
            <div className="border rounded-lg p-6 bg-gray-50">
              {!isPremium && (
                <div className="flex justify-between items-center mb-4">
                  {/* <h3 className="text-lg font-semibold text-gray-800">
                    Current Discount Settings
                  </h3> */}
                  <button
                    onClick={openModal}
                    className="theme-button-gradient w-full px-4 py-2 rounded-lg flex items-center space-x-2 hover:shadow-lg transition-all"
                    title="Edit Discount"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Discount</span>
                  </button>
                </div>
              )}

              {business.active_discount ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-lg border">
                    <p className="text-sm text-gray-600 flex items-center gap-2 mb-2">
                      <BadgePercent className="h-4 w-4" /> Discount Text
                    </p>
                    <p className="font-semibold text-lg text-gray-900">
                      {business.active_discount.discount_text}
                    </p>
                  </div>
                  {isPremium &&
                    <>
                      <div className="bg-white p-4 rounded-lg border">
                        <p className="text-sm text-gray-600 mb-2">
                          Total Discount Offered
                        </p>
                        <p className="font-semibold text-2xl text-blue-600">
                          {business.active_discount.fixed_discount}%
                        </p>
                      </div>
                      {/* <div className="bg-white p-4 rounded-lg border">
                        <p className="text-sm text-gray-600 mb-2">
                          Customer Discount
                        </p>
                        <p className="font-semibold text-2xl text-green-600">
                          {business.active_discount.customer_discount}%
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg border">
                        <p className="text-sm text-gray-600 mb-2">
                          Company Commission
                        </p>
                        <p className="font-semibold text-2xl text-orange-600">
                          {business.active_discount.company_commission}%
                        </p>
                      </div> */}
                    </>
                  }
                </div>
              ) : (
                <div className="text-center py-12">
                  <BadgePercent className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Active Discount
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Set up your discount information to start offering deals to
                    customers
                  </p>
                  {!isPremium && (
                    <button
                      onClick={openModal}
                      className="theme-button-gradient px-6 py-3 rounded-lg flex items-center space-x-2 mx-auto hover:shadow-lg transition-all"
                    >
                      <BadgePercent className="h-5 w-5" />
                      <span>Set Up Discount</span>
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Discount Information Help */}
            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">Understanding Discount Settings</h4>
              <div className="space-y-3 text-sm text-blue-800">
                <div>
                  <strong>Discount Text:</strong> The promotional message that customers will see
                </div>
                <div>
                  <strong>Fixed Discount:</strong> The base discount percentage for your business
                </div>
                <div>
                  <strong>Customer Discount:</strong> The actual discount percentage customers receive
                </div>
                <div>
                  <strong>Company Commission:</strong> The percentage that goes to the platform
                </div>
              </div>
            </div> */}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No business details found</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <ReusableModal
        open={modalState.open}
        onOpenChange={closeModal}
        type={modalState.type}
        data={business}
        loading={modalState.loading}
        showActions={false}
        size="large"
      >
        <BusinessOwnerDiscountEditForm
          data={business}
          onSave={handleSaveDiscount}
          loading={modalState.loading}
        />
      </ReusableModal>
    </div>
  );
}
